import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const PI = Math.PI;

/* --- shared frame --- */
const Frame = ({ children }) => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="relative w-full max-w-3xl aspect-[4/3] bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden shadow-inner">
      {children}
    </div>
  </div>
);
const Info = ({ items }) => (
  <div className="absolute bottom-3 left-4 right-4 flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-cyan-300">
    {items.map((t, i) => <span key={i}>{t}</span>)}
  </div>
);
const PlaceholderSim = ({ msg = "Interactive simulation coming soon." }) => (
  <Frame><div className="text-slate-500 font-mono text-sm p-6 text-center">{msg}</div></Frame>
);

/* ---------- PENDULUM ---------- */
const PendulumSim = ({ length = 100, gravity = 9.8, angle = 10 }) => {
  const L = Math.max(20, Math.min(120, +length));
  const g = +gravity;
  const T = 2 * PI * Math.sqrt(L / 100 / g);
  const dur = `${T.toFixed(2)}s`;
  return (
    <Frame>
      <div className="absolute inset-0 flex justify-center pt-6">
        <div className="relative" style={{ width: 220 }}>
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-6 h-2 bg-slate-500 rounded" />
          <div style={{ transformOrigin: "top center", animation: `pendulumSwing${Math.round(angle)} ${dur} ease-in-out infinite` }} className="relative mx-auto">
            <div style={{ height: L * 2.4, width: 2 }} className="mx-auto bg-slate-400" />
            <div style={{ width: 34, height: 34 }} className="mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg -mt-1" />
          </div>
        </div>
      </div>
      <Info items={[`l = ${L} cm`, `T ≈ ${T.toFixed(2)} s`, `g = ${g} m/s²`]} />
      <style>{`@keyframes pendulumSwing${Math.round(angle)} { 0%,100% { transform: rotate(-${angle}deg);} 50% { transform: rotate(${angle}deg);} }`}</style>
    </Frame>
  );
};

/* ---------- OHM ---------- */
const OhmSim = ({ voltage = 6, resistance = 20 }) => {
  const I = voltage / Math.max(resistance, 0.01);
  const data = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ V: i, I: (i / Math.max(resistance, 0.01)).toFixed(3) })), [resistance]);
  const brightness = Math.min(1, I / 0.5);
  return (
    <Frame>
      <div className="absolute inset-0 p-6 grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-slate-800 border-4 border-slate-700 flex items-center justify-center relative">
            <div className="w-14 h-14 rounded-full transition-all" style={{ background: `radial-gradient(circle, rgba(255, 220, 100, ${brightness}) 0%, rgba(255, 180, 60, ${brightness * 0.5}) 60%, rgba(30,30,30,1) 100%)`, boxShadow: `0 0 ${brightness * 40}px rgba(255,200,80,${brightness})` }} />
          </div>
          <div className="mt-3 text-cyan-300 font-mono text-sm">I = {I.toFixed(3)} A</div>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="V" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} />
            <Line type="monotone" dataKey="I" stroke="#22d3ee" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Frame>
  );
};

/* ---------- REFLECTION ---------- */
const ReflectionSim = ({ incidence = 30 }) => {
  const i = +incidence, rad = (i * PI) / 180, cx = 200, cy = 220, len = 150;
  return (
    <Frame>
      <svg viewBox="0 0 400 300" className="w-full h-full">
        <rect x="80" y="215" width="240" height="14" fill="#0891b2" />
        <line x1={cx} y1={cy - 100} x2={cx} y2={cy} stroke="#94a3b8" strokeDasharray="4 4" />
        <line x1={cx - Math.sin(rad) * len} y1={cy - Math.cos(rad) * len} x2={cx} y2={cy} stroke="#fde047" strokeWidth="2" />
        <line x1={cx} y1={cy} x2={cx + Math.sin(rad) * len} y2={cy - Math.cos(rad) * len} stroke="#f472b6" strokeWidth="2" />
      </svg>
      <Info items={[`∠i = ${i}°`, `∠r = ${i}°`]} />
    </Frame>
  );
};

/* ---------- DENSITY ---------- */
const DensitySim = ({ length = 8, breadth = 5, thickness = 2, mass = 200 }) => {
  const V = length * breadth * thickness, rho = mass / V;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-8">
        <div style={{ width: length * 14, height: breadth * 14, background: "linear-gradient(135deg,#38bdf8,#0369a1)", transform: "perspective(600px) rotateY(-25deg) rotateX(10deg)", borderRadius: 8, boxShadow: "0 20px 40px rgba(0,0,0,.5)" }} />
        <div className="text-left text-cyan-200 font-mono text-sm space-y-1">
          <div>V = {V.toFixed(2)} cm³</div>
          <div>m = {mass} g</div>
          <div className="text-amber-300 pt-2 border-t border-slate-700">ρ = {rho.toFixed(3)} g/cm³</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- CONVEX LENS ---------- */
const ConvexLensSim = ({ objectDist = 25, focalLength = 15 }) => {
  const u = +objectDist, f = +focalLength;
  const v = u !== f ? 1 / (1 / f - 1 / u) : 100;
  const mag = -v / u, cx = 250, scale = 3;
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="20" y1="150" x2="480" y2="150" stroke="#475569" />
        <ellipse cx={cx} cy="150" rx="14" ry="80" fill="rgba(34,211,238,0.15)" stroke="#22d3ee" strokeWidth="2" />
        <line x1={cx - u * scale} y1="150" x2={cx - u * scale} y2="90" stroke="#fbbf24" strokeWidth="3" />
        <line x1={cx + v * scale} y1="150" x2={cx + v * scale} y2={150 + mag * 60} stroke="#f472b6" strokeWidth="3" />
        <circle cx={cx - f * scale} cy="150" r="3" fill="#94a3b8" />
        <circle cx={cx + f * scale} cy="150" r="3" fill="#94a3b8" />
      </svg>
      <Info items={[`u = ${u} cm`, `v = ${v.toFixed(1)} cm`, `f = ${((u * v) / (u + v || 1)).toFixed(1)} cm`]} />
    </Frame>
  );
};

/* ---------- PRISM ---------- */
const PrismSim = ({ incidence = 50, prismAngle = 60, refractiveIndex = 1.5 }) => {
  const i1 = +incidence, A = +prismAngle, n = +refractiveIndex;
  const r1 = Math.asin(Math.sin((i1 * PI) / 180) / n) * 180 / PI;
  const r2 = A - r1;
  const arg = n * Math.sin((r2 * PI) / 180);
  const i2 = Math.abs(arg) <= 1 ? Math.asin(arg) * 180 / PI : 0;
  const delta = i1 + i2 - A;
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <polygon points="250,60 170,240 330,240" fill="rgba(56,189,248,0.14)" stroke="#38bdf8" strokeWidth="2" />
        <line x1="60" y1="180" x2="210" y2="150" stroke="#fde047" strokeWidth="2" />
        <line x1="210" y1="150" x2="290" y2="180" stroke="#fb923c" strokeWidth="2" />
        <line x1="290" y1="180" x2={290 + 160 * Math.cos(delta * PI / 180)} y2={180 + 160 * Math.sin(delta * PI / 180)} stroke="#f472b6" strokeWidth="2" />
      </svg>
      <Info items={[`i₁=${i1}°`, `r₁=${r1.toFixed(1)}°`, `i₂=${i2.toFixed(1)}°`, `δ=${delta.toFixed(1)}°`]} />
    </Frame>
  );
};

/* ---------- LEVER ---------- */
const LeverSim = ({ load = 100, loadArm = 20, effortArm = 30 }) => {
  const E = (load * loadArm) / effortArm;
  const tilt = Math.max(-6, Math.min(6, (load * loadArm - E * effortArm) / 50));
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="relative" style={{ width: 400 }}>
          <div className="h-2 bg-slate-500 rounded" style={{ transform: `rotate(${tilt}deg)`, transformOrigin: `${loadArm * 8}px center` }} />
          <div className="absolute" style={{ left: loadArm * 8 - 12, top: 8 }}><div className="w-0 h-0 border-l-[14px] border-r-[14px] border-b-[22px] border-l-transparent border-r-transparent border-b-slate-400" /></div>
          <div className="absolute -top-14 text-center text-cyan-300 font-mono text-xs" style={{ left: 0 }}><div className="w-14 h-14 rounded bg-fuchsia-500 shadow-lg" /><div className="mt-1">L={load}g</div></div>
          <div className="absolute -top-14 text-center text-amber-300 font-mono text-xs" style={{ right: 0 }}><div className="w-14 h-14 rounded bg-amber-500 shadow-lg" /><div className="mt-1">E={E.toFixed(0)}g</div></div>
        </div>
        <div className="mt-8 text-xs font-mono text-cyan-200 flex gap-6"><span>MA = {(load / E).toFixed(2)}</span><span>VR = {(effortArm / loadArm).toFixed(2)}</span></div>
      </div>
    </Frame>
  );
};

/* ---------- BOYLE ---------- */
const BoyleSim = ({ volume = 40, constantK = 800 }) => {
  const V = +volume, k = +constantK, P = k / V;
  const data = useMemo(() => Array.from({ length: 20 }, (_, i) => { const v = 5 + i * 5; return { V: v, P: (k / v).toFixed(2) }; }), [k]);
  return (
    <Frame>
      <div className="absolute inset-0 p-4 grid grid-cols-2 gap-3">
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-24 h-72 rounded-lg border-2 border-slate-500 bg-slate-800/50 overflow-hidden flex flex-col-reverse">
            <div className="bg-gradient-to-t from-cyan-400/70 to-cyan-300/30 transition-all" style={{ height: `${V}%` }} />
          </div>
          <div className="mt-3 text-cyan-300 font-mono text-sm">V = {V} cm³ · P = {P.toFixed(2)} kPa</div>
        </div>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="V" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Line type="monotone" dataKey="P" stroke="#f472b6" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Frame>
  );
};

/* ---------- FRICTION ---------- */
const FrictionSim = ({ normal = 10, mu = 0.35 }) => {
  const f = normal * mu;
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="20" y1="220" x2="480" y2="220" stroke="#475569" strokeWidth="2" />
        <rect x="150" y="170" width="120" height="50" fill="#3b82f6" stroke="#1e40af" strokeWidth="2" rx="4" />
        <line x1="270" y1="195" x2="420" y2="195" stroke="#f472b6" strokeWidth="2" />
        <polygon points="420,195 410,190 410,200" fill="#f472b6" />
        <text x="340" y="188" fill="#f472b6" fontSize="12" fontFamily="monospace">f = {f.toFixed(2)} N</text>
        <line x1="210" y1="170" x2="210" y2="110" stroke="#22d3ee" strokeWidth="2" />
        <polygon points="210,110 205,120 215,120" fill="#22d3ee" />
        <text x="216" y="140" fill="#22d3ee" fontSize="12" fontFamily="monospace">N = {normal} N</text>
      </svg>
      <Info items={[`μ = ${mu}`, `f = μN`]} />
    </Frame>
  );
};

/* ---------- METER BRIDGE ---------- */
const MeterBridgeSim = ({ unknownR = 8, knownS = 10 }) => {
  const l = (100 * unknownR) / (unknownR + knownS);
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="60" y1="200" x2="440" y2="200" stroke="#94a3b8" strokeWidth="4" />
        {Array.from({ length: 11 }).map((_, i) => <line key={i} x1={60 + i * 38} y1="210" x2={60 + i * 38} y2="220" stroke="#64748b" />)}
        <line x1={60 + l * 3.8} y1="160" x2={60 + l * 3.8} y2="200" stroke="#22d3ee" strokeWidth="2" />
        <circle cx={60 + l * 3.8} cy="155" r="6" fill="#22d3ee" />
        <rect x="80" y="90" width="80" height="40" fill="#7c3aed" stroke="#a78bfa" />
        <text x="120" y="115" fill="#fff" textAnchor="middle" fontSize="14" fontFamily="monospace">R = {unknownR}Ω</text>
        <rect x="340" y="90" width="80" height="40" fill="#0891b2" stroke="#67e8f9" />
        <text x="380" y="115" fill="#fff" textAnchor="middle" fontSize="14" fontFamily="monospace">S = {knownS}Ω</text>
        <circle cx="250" cy="90" r="18" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
        <text x="250" y="94" fill="#22d3ee" textAnchor="middle" fontSize="12" fontFamily="monospace">G</text>
      </svg>
      <Info items={[`l = ${l.toFixed(1)} cm`, `R/S = l/(100−l)`]} />
    </Frame>
  );
};

/* ---------- DIODE ---------- */
const DiodeSim = ({ voltage = 0.7, cutIn = 0.7 }) => {
  const data = useMemo(() => {
    const arr = [];
    for (let v = -3; v <= 1.2; v += 0.05) {
      let i = 0;
      if (v > cutIn) i = (v - cutIn) * 100;
      else if (v < -2) i = -0.2 - (Math.abs(v) - 2) * 2;
      else i = -0.02;
      arr.push({ V: v.toFixed(2), I: i.toFixed(3) });
    }
    return arr;
  }, [cutIn]);
  return (
    <Frame>
      <div className="absolute inset-0 p-4">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="V" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <ReferenceLine x={String((+voltage).toFixed(2))} stroke="#f472b6" />
            <Line type="monotone" dataKey="I" stroke="#22d3ee" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Info items={[`V = ${voltage} V`, `Cut-in = ${cutIn} V`]} />
    </Frame>
  );
};

/* ---------- PH ---------- */
const PhSim = ({ sample = "Water" }) => {
  const table = { "Lemon juice": 2.2, "Vinegar": 3.0, "Water": 7.0, "Baking soda": 8.5, "Soap solution": 10.0, "Milk of magnesia": 10.5 };
  const ph = table[sample] ?? 7;
  const colors = ["#dc2626","#ef4444","#f97316","#f59e0b","#eab308","#84cc16","#22c55e","#10b981","#14b8a6","#06b6d4","#0ea5e9","#3b82f6","#6366f1","#8b5cf6"];
  return (
    <Frame>
      <div className="absolute inset-0 p-6 flex flex-col justify-center items-center gap-6">
        <div className="w-40 h-56 rounded-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
          <div className="h-3/4 transition-colors" style={{ backgroundColor: colors[Math.max(0, Math.min(13, Math.round(ph)))] }} />
        </div>
        <div className="text-center">
          <div className="text-lg text-cyan-200 font-outfit font-bold">{sample}</div>
          <div className="text-4xl font-outfit font-black text-amber-300">pH = {ph.toFixed(1)}</div>
          <div className="text-sm text-slate-400 font-mono">{ph < 7 ? "Acidic" : ph > 7 ? "Basic" : "Neutral"}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- TITRATION ---------- */
const TitrationSim = ({ hclConc = 0.1, naohVol = 25, naohConc = 0.12 }) => {
  const equivalenceVol = (naohConc * naohVol) / hclConc;
  const [added, setAdded] = React.useState(0);
  React.useEffect(() => {
    let raf, start = performance.now();
    const step = (now) => {
      const t = (now - start) / 40;
      if (t <= equivalenceVol) { setAdded(t); raf = requestAnimationFrame(step); }
      else setAdded(equivalenceVol);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [equivalenceVol]);
  const near = Math.abs(added - equivalenceVol) < 0.5;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-end justify-center gap-8 p-6">
        <div className="flex flex-col items-center">
          <div className="w-8 h-40 bg-slate-800/70 border-2 border-slate-500 rounded-b overflow-hidden flex flex-col-reverse">
            <div className="bg-cyan-400/70 transition-all" style={{ height: `${100 - (added / equivalenceVol) * 100}%` }} />
          </div>
          <div className="w-1 h-6 bg-slate-500" />
          <div className="text-xs font-mono text-cyan-200 mt-2">Added {added.toFixed(1)} mL</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-6 h-14 bg-slate-800/70 border-2 border-slate-500 border-b-0" />
          <div className="w-40 h-40 rounded-b-full border-2 border-slate-500 relative overflow-hidden" style={{ background: near ? "linear-gradient(to top, #fb7185, #fb7185, transparent)" : "linear-gradient(to top, rgba(147,197,253,.5), transparent)" }} />
        </div>
      </div>
      <div className="absolute bottom-2 left-0 right-0 text-center text-xs font-mono">
        <span className={near ? "text-pink-300" : "text-slate-400"}>{near ? "Endpoint reached — pink!" : "Titrating..."}</span>
      </div>
    </Frame>
  );
};

/* ---------- SODIUM WATER ---------- */
const SodiumSim = ({ sodiumSize = 0.5 }) => (
  <Frame>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="relative w-80 h-40 rounded-b-3xl border-2 border-slate-500 bg-blue-500/20 overflow-hidden">
        <div className="absolute inset-x-0 top-2 h-3 bg-cyan-300/40" />
        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-10 h-6 bg-slate-100 rounded shadow-lg anim-float" style={{ transform: `translateX(-50%) scale(${1 + sodiumSize})` }} />
        {[...Array(10)].map((_, i) => <div key={i} className="absolute rounded-full bg-white/60 anim-float" style={{ left: `${20 + i * 6}%`, bottom: `${10 + (i % 4) * 6}px`, width: 6 + i, height: 6 + i, animationDelay: `${i * 0.2}s` }} />)}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-14 rounded-full bg-gradient-to-t from-orange-500 via-yellow-400 to-transparent blur-[6px] anim-pulse-glow" />
      </div>
      <div className="mt-4 text-xs font-mono text-amber-300 max-w-md text-center">2Na + 2H₂O → 2NaOH + H₂↑ · (Virtual only!)</div>
    </div>
  </Frame>
);

/* ---------- ELECTROLYSIS ---------- */
const ElectrolysisSim = ({ voltage = 6, time = 5 }) => {
  const h2 = Math.min(80, voltage * time * 1.5); const o2 = h2 / 2;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-end justify-center gap-6 pb-8">
        <div className="flex flex-col items-center">
          <div className="w-14 h-56 rounded-t-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
            <div className="bg-cyan-400/60 transition-all" style={{ height: `${100 - h2}%` }} />
          </div>
          <div className="text-xs font-mono text-cyan-300 mt-2">H₂ (cathode)</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-14 h-56 rounded-t-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
            <div className="bg-cyan-400/60 transition-all" style={{ height: `${100 - o2}%` }} />
          </div>
          <div className="text-xs font-mono text-pink-300 mt-2">O₂ (anode)</div>
        </div>
      </div>
      <Info items={[`V = ${voltage} V`, `t = ${time} min`, `Ratio ≈ 2:1`]} />
    </Frame>
  );
};

/* ---------- MICROSCOPE ---------- */
const MicroscopeSim = ({ magnification = 100, specimen = "Onion peel" }) => {
  const size = 40 + magnification / 4;
  const isOnion = specimen === "Onion peel";
  const isCheek = specimen === "Cheek cells";
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-72 h-72 rounded-full bg-slate-100 border-8 border-slate-700 relative overflow-hidden shadow-inner">
          {isOnion && Array.from({ length: 30 }).map((_, i) => (
            <div key={i} className="absolute border border-purple-400 bg-purple-100/60 rounded" style={{ left: `${(i % 6) * 16 + 5}%`, top: `${Math.floor(i / 6) * 16 + 5}%`, width: `${size / 4}px`, height: `${size / 6}px` }}>
              <div className="w-2 h-2 rounded-full bg-purple-700 m-1" />
            </div>
          ))}
          {isCheek && Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="absolute rounded-full bg-pink-200/70 border border-pink-400" style={{ left: `${20 + (i % 4) * 20}%`, top: `${20 + Math.floor(i / 4) * 22}%`, width: size / 2, height: size / 2 }}>
              <div className="w-2 h-2 rounded-full bg-pink-700 m-3" />
            </div>
          ))}
          {!isOnion && !isCheek && Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="absolute rounded bg-emerald-200/80 border border-emerald-500" style={{ left: `${(i % 5) * 18 + 5}%`, top: `${Math.floor(i / 5) * 22 + 5}%`, width: size / 3, height: size / 3 }} />
          ))}
        </div>
      </div>
      <Info items={[`${magnification}×`, specimen]} />
    </Frame>
  );
};

/* ---------- PHOTOSYNTHESIS ---------- */
const PhotoSim = ({ light = 60, duration = 6 }) => {
  const starch = Math.min(100, (light * duration) / 8);
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-10">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 shadow-2xl mx-auto" style={{ boxShadow: `0 0 ${40 + light}px rgba(253,224,71,${light/100})` }} />
          <div className="mt-2 text-xs font-mono text-amber-300">☀ {light}%</div>
        </div>
        <div className="text-center">
          <div className="relative w-40 h-40">
            <div className="absolute inset-0 rounded-[50%] bg-emerald-500 shadow-xl" style={{ transform: "rotate(30deg)" }} />
            <div className="absolute inset-0 rounded-[50%]" style={{ transform: "rotate(30deg)", background: `radial-gradient(circle at 40% 40%, rgba(30, 58, 138, ${starch/150}), transparent 60%)` }} />
          </div>
          <div className="mt-2 text-xs font-mono text-emerald-300">Starch: {starch.toFixed(0)}%</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- OSMOSIS ---------- */
const OsmosisSim = ({ sugarConc = 20, time = 60 }) => {
  const rise = Math.min(80, (sugarConc * time) / 80);
  return (
    <Frame>
      <div className="absolute inset-0 flex items-end justify-center pb-10">
        <div className="relative w-56 h-64 rounded-lg border-2 border-slate-500 bg-blue-400/20 overflow-hidden">
          <div className="absolute inset-x-0 bottom-0 bg-blue-400/50" style={{ height: "60%" }} />
          <div className="absolute left-1/2 -translate-x-1/2 bottom-8 w-24 h-24 rounded-full bg-gradient-to-br from-amber-300 to-amber-700 shadow-inner">
            <div className="absolute inset-4 rounded-full bg-slate-900/40" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-4 w-8 bg-fuchsia-400 rounded" style={{ height: `${rise}%`, maxHeight: 60 }} />
          </div>
        </div>
      </div>
      <Info items={[`sugar rose ${rise.toFixed(0)}% over ${time} min`]} />
    </Frame>
  );
};

/* ---------- DNA EXTRACT ---------- */
const DnaSim = ({ detergent = 5, salt = 2 }) => {
  const y = Math.min(100, detergent * 8 + salt * 6);
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-8">
        <div className="w-24 h-56 rounded-b-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
          <div className="bg-pink-300/60" style={{ height: "60%" }} />
          <div className="bg-cyan-300/50" style={{ height: "40%" }} />
          {[...Array(6)].map((_, i) => <div key={i} className="absolute w-1 rounded-full bg-white/90 anim-float" style={{ left: `${20 + i * 10}%`, top: `${20 + i * 3}%`, height: `${y/2}%`, animationDelay: `${i * 0.2}s`, opacity: y/100 }} />)}
        </div>
        <div className="text-cyan-200 font-mono text-sm space-y-1">
          <div>Detergent: {detergent} mL</div><div>Salt: {salt} g</div>
          <div className="text-fuchsia-300 pt-2 border-t border-slate-700">Yield: {y.toFixed(0)}%</div>
        </div>
      </div>
    </Frame>
  );
};

/* =========================================================================
   NEW SIMULATION KINDS
   ========================================================================= */

/* ---------- VERNIER CALLIPER ---------- */
const VernierSim = ({ msr = 2.3, vsd = 6 }) => {
  const reading = (+msr) + (+vsd) * 0.01;
  return (
    <Frame>
      <div className="absolute inset-0 p-6 flex flex-col justify-center items-center gap-4">
        <svg viewBox="0 0 500 140" className="w-full max-w-lg">
          <rect x="10" y="10" width="480" height="30" fill="#334155" rx="4" />
          {Array.from({ length: 50 }).map((_, i) => <line key={i} x1={20 + i * 9.4} y1="10" x2={20 + i * 9.4} y2={i % 5 === 0 ? 30 : 22} stroke="#f1f5f9" />)}
          <rect x={20 + msr * 47} y="45" width="140" height="30" fill="#0891b2" rx="4" />
          {Array.from({ length: 11 }).map((_, i) => <line key={i} x1={22 + msr * 47 + i * 13.6} y1="45" x2={22 + msr * 47 + i * 13.6} y2={i === +vsd ? 90 : 60} stroke="#fde047" strokeWidth={i === +vsd ? 2 : 1} />)}
        </svg>
        <div className="text-cyan-200 font-mono text-sm">MSR = {(+msr).toFixed(1)} cm · VSD coincidence = {vsd}</div>
        <div className="text-amber-300 font-outfit font-bold text-2xl">Reading = {reading.toFixed(3)} cm</div>
      </div>
    </Frame>
  );
};

/* ---------- SCREW GAUGE ---------- */
const ScrewGaugeSim = ({ psr = 1.5, hsr = 24 }) => {
  const reading = (+psr) + (+hsr) * 0.01;
  return (
    <Frame>
      <div className="absolute inset-0 p-6 flex flex-col justify-center items-center gap-4">
        <div className="relative w-96 h-24 rounded-full bg-slate-700 flex items-center px-6">
          <div className="w-16 h-16 rounded-full bg-slate-800 border-4 border-slate-600 flex items-center justify-center text-cyan-300 font-mono text-sm">
            {(+psr).toFixed(1)}
          </div>
          <div className="flex-1 h-8 mx-4 bg-slate-800 rounded-full flex items-center relative">
            <div className="absolute inset-y-0" style={{ left: `${(hsr/100)*100}%`, width: 2, background: "#fde047" }} />
          </div>
          <div className="w-16 h-16 rounded-full bg-slate-800 border-4 border-slate-600 flex items-center justify-center text-amber-300 font-mono text-sm">
            {hsr}
          </div>
        </div>
        <div className="text-cyan-200 font-mono text-sm">Pitch scale = {psr} mm · Head scale = {hsr}</div>
        <div className="text-amber-300 font-outfit font-bold text-2xl">Diameter = {reading.toFixed(3)} mm</div>
        <div className="text-slate-400 font-mono text-xs">Radius = {(reading/2).toFixed(3)} mm</div>
      </div>
    </Frame>
  );
};

/* ---------- HEAT ACTION ---------- */
const heatMap = {
  "Copper carbonate": { color: "#166534", newColor: "#0f172a", gas: "CO₂ evolved", residue: "Black CuO", eq: "CuCO₃ → CuO + CO₂↑" },
  "Zinc carbonate": { color: "#f8fafc", newColor: "#fde047", gas: "CO₂ evolved", residue: "Yellow ZnO (hot) / white (cold)", eq: "ZnCO₃ → ZnO + CO₂↑" },
  "Washing soda": { color: "#e0f2fe", newColor: "#f8fafc", gas: "H₂O vapour", residue: "Anhydrous Na₂CO₃", eq: "Na₂CO₃·10H₂O → Na₂CO₃ + 10H₂O↑" },
  "Copper sulphate crystals": { color: "#38bdf8", newColor: "#f8fafc", gas: "H₂O vapour", residue: "White CuSO₄", eq: "CuSO₄·5H₂O → CuSO₄ + 5H₂O↑" },
  "Zinc nitrate": { color: "#fef9c3", newColor: "#f8fafc", gas: "Brown NO₂ + O₂", residue: "Yellow ZnO", eq: "2Zn(NO₃)₂ → 2ZnO + 4NO₂↑ + O₂↑" },
  "Copper nitrate": { color: "#60a5fa", newColor: "#0f172a", gas: "Brown NO₂ + O₂", residue: "Black CuO", eq: "2Cu(NO₃)₂ → 2CuO + 4NO₂↑ + O₂↑" },
  "Lead nitrate": { color: "#f8fafc", newColor: "#fbbf24", gas: "Brown NO₂ + O₂ (decrepitates)", residue: "Yellow PbO", eq: "2Pb(NO₃)₂ → 2PbO + 4NO₂↑ + O₂↑" },
  "Ammonium chloride": { color: "#f8fafc", newColor: "#e2e8f0", gas: "Sublimes (recondenses)", residue: "None", eq: "NH₄Cl ⇌ NH₃ + HCl" },
  "Iodine": { color: "#7c3aed", newColor: "#a78bfa", gas: "Violet vapours (sublimes)", residue: "None", eq: "I₂ (s) → I₂ (g)" },
  "Ammonium dichromate": { color: "#fb923c", newColor: "#065f46", gas: "N₂ + H₂O ('volcano')", residue: "Green Cr₂O₃", eq: "(NH₄)₂Cr₂O₇ → Cr₂O₃ + N₂↑ + 4H₂O↑" },
};
const HeatActionSim = ({ compound = "Copper carbonate" }) => {
  const info = heatMap[compound] || heatMap["Copper carbonate"];
  const [hot, setHot] = React.useState(false);
  React.useEffect(() => { setHot(false); const t = setTimeout(() => setHot(true), 400); return () => clearTimeout(t); }, [compound]);
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6">
        <div className="relative">
          <div className="w-14 h-40 rounded-b-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden">
            <div className="absolute inset-x-1 bottom-1 h-16 rounded transition-colors duration-1000" style={{ background: hot ? info.newColor : info.color }} />
            {hot && [...Array(5)].map((_, i) => <div key={i} className="absolute w-2 h-2 rounded-full bg-white/40 anim-float" style={{ left: `${20 + i*15}%`, top: `-10px`, animationDelay: `${i*0.3}s` }} />)}
          </div>
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-24 h-8 rounded-full" style={{ background: "radial-gradient(ellipse, #f97316 0%, transparent 70%)", filter: "blur(4px)" }} />
        </div>
        <div className="text-center">
          <div className="font-outfit font-bold text-cyan-200">{compound}</div>
          <div className="mt-2 text-xs text-amber-300 font-mono">{info.gas}</div>
          <div className="text-xs text-emerald-300 font-mono">Residue: {info.residue}</div>
          <div className="mt-2 text-xs text-slate-400 font-mono">{info.eq}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- FLAME TEST ---------- */
const flameColors = {
  "Sodium salt":    { c: "#facc15", ion: "Na⁺" },
  "Potassium salt": { c: "#a78bfa", ion: "K⁺" },
  "Calcium salt":   { c: "#dc2626", ion: "Ca²⁺" },
  "Copper salt":    { c: "#10b981", ion: "Cu²⁺" },
  "Barium salt":    { c: "#84cc16", ion: "Ba²⁺" },
  "Strontium salt": { c: "#ef4444", ion: "Sr²⁺" },
};
const FlameTestSim = ({ salt = "Sodium salt" }) => {
  const info = flameColors[salt] || flameColors["Sodium salt"];
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-10">
        {/* Burner */}
        <div className="w-32 h-14 bg-gradient-to-b from-slate-700 to-slate-900 rounded" />
        <div className="w-6 h-16 bg-slate-800 -mt-1" />
        {/* Flame */}
        <div className="absolute inset-x-0 top-16 flex justify-center">
          <div className="relative w-14 h-40 anim-float">
            <div className="absolute inset-0 rounded-full opacity-90" style={{ background: `radial-gradient(circle at 50% 80%, ${info.c}, #f97316 40%, transparent 80%)`, filter: "blur(2px)" }} />
            <div className="absolute inset-x-3 top-6 bottom-8 rounded-full" style={{ background: `radial-gradient(circle, ${info.c}, transparent 70%)`, filter: "blur(1px)" }} />
          </div>
        </div>
        <div className="mt-4 text-center">
          <div className="font-outfit font-bold text-cyan-200 text-lg">{salt}</div>
          <div className="text-amber-300 font-mono text-sm">Flame: {info.c}</div>
          <div className="text-emerald-300 font-mono">Ion: {info.ion}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- REACTION-CARD  (double-displacement / acid-action / cation-test / functional-group / biomol / food / heat-action reuse) ---------- */
const doubleDispData = {
  "Na₂SO₄ + BaCl₂": { color: "#f8fafc", desc: "White ppt of BaSO₄" },
  "AgNO₃ + NaCl":   { color: "#e2e8f0", desc: "Curdy white AgCl" },
  "Pb(NO₃)₂ + KI":  { color: "#fbbf24", desc: "Yellow PbI₂ (golden shower)" },
};
const DoubleDispSim = ({ reaction = "Pb(NO₃)₂ + KI" }) => {
  const info = doubleDispData[reaction] || doubleDispData["Pb(NO₃)₂ + KI"];
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div className="w-40 h-52 rounded-b-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
          <div className="transition-all duration-700" style={{ height: "60%", background: info.color }} />
          {[...Array(20)].map((_, i) => <div key={i} className="absolute rounded-full anim-float" style={{ left: `${5 + (i%5)*18}%`, bottom: `${5 + (i%4)*10}px`, width: 3+i%3, height: 3+i%3, background: info.color, animationDelay: `${i*0.1}s` }} />)}
        </div>
        <div className="text-center">
          <div className="font-outfit font-bold text-cyan-200 text-lg">{reaction}</div>
          <div className="text-amber-300 font-mono text-sm mt-1">{info.desc}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- ACID ACTION / CATION TEST — generic reaction card ---------- */
const ReactionCardSim = ({ title, subtitle, gasLabel, color = "#38bdf8" }) => (
  <Frame>
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
      <div className="w-32 h-48 rounded-b-lg border-2 border-slate-500 bg-slate-800/60 relative overflow-hidden flex flex-col-reverse">
        <div className="transition-colors duration-700" style={{ height: "60%", background: color }} />
        {[...Array(12)].map((_, i) => <div key={i} className="absolute rounded-full bg-white/70 anim-float" style={{ left: `${15 + (i%5)*15}%`, bottom: `${5 + (i%4)*10}px`, width: 4, height: 4, animationDelay: `${i*0.15}s` }} />)}
      </div>
      {gasLabel && <div className="text-cyan-300 font-mono text-sm">↑ {gasLabel}</div>}
      <div className="text-center">
        <div className="font-outfit font-bold text-cyan-200">{title}</div>
        {subtitle && <div className="mt-1 text-amber-300 font-mono text-sm max-w-md">{subtitle}</div>}
      </div>
    </div>
  </Frame>
);
const acidActionMap = {
  "Zinc (metal)":     { c: "#94a3b8", g: "H₂", d: "Effervescence — H₂ pops with splint" },
  "Sodium carbonate": { c: "#fef3c7", g: "CO₂", d: "Turns lime water milky" },
  "Sodium sulphide":  { c: "#000",    g: "H₂S", d: "Rotten-egg smell; black on Pb-acetate paper" },
  "Sodium sulphite":  { c: "#fef9c3", g: "SO₂", d: "Turns K₂Cr₂O₇ green" },
  "Copper oxide (CuO)": { c: "#22d3ee", g: "", d: "Blue-green CuCl₂ solution" },
  "Manganese dioxide (MnO₂)": { c: "#eab308", g: "Cl₂", d: "Yellow-green Cl₂; bleaches damp litmus" },
};
const AcidActionSim = ({ substrate = "Zinc (metal)" }) => {
  const info = acidActionMap[substrate] || acidActionMap["Zinc (metal)"];
  return <ReactionCardSim title={substrate} subtitle={info.d} gasLabel={info.g} color={info.c} />;
};

const cationTestMap = {
  "Cu²⁺,NaOH":  { c: "#93c5fd", d: "Pale blue ppt — insoluble in excess" },
  "Cu²⁺,NH₄OH": { c: "#1e40af", d: "Blue ppt → deep blue in excess (tetraamine)" },
  "Fe²⁺,NaOH":  { c: "#4d7c0f", d: "Dirty green ppt → brown on air" },
  "Fe³⁺,NaOH":  { c: "#b91c1c", d: "Reddish brown ppt" },
  "Pb²⁺,NaOH":  { c: "#f8fafc", d: "White ppt, soluble in excess (amphoteric)" },
  "Zn²⁺,NaOH":  { c: "#f1f5f9", d: "White ppt, soluble in excess" },
  "Zn²⁺,NH₄OH": { c: "#f1f5f9", d: "White ppt, soluble in excess ammonia" },
  "Mg²⁺,NaOH":  { c: "#f8fafc", d: "White ppt, insoluble in excess" },
  "Ca²⁺,NaOH":  { c: "#f8fafc", d: "White ppt, insoluble in excess" },
  "NH₄⁺,NaOH":  { c: "#fef9c3", d: "No ppt; NH₃ evolves on warming" },
};
const CationTestSim = ({ cation = "Cu²⁺", reagent = "NaOH" }) => {
  const info = cationTestMap[`${cation},${reagent}`] || { c: "#94a3b8", d: "See standard table." };
  return <ReactionCardSim title={`${cation} + ${reagent}`} subtitle={info.d} color={info.c} />;
};

/* ---------- FOOD TEST ---------- */
const foodMap = {
  "Glucose,Benedict's":       { c: "#dc2626", d: "Brick-red ppt — reducing sugar" },
  "Glucose solution,Benedict's": { c: "#dc2626", d: "Brick-red ppt" },
  "Sucrose,Benedict's":       { c: "#7dd3fc", d: "No change — non-reducing" },
  "Starch,Iodine":            { c: "#1e40af", d: "Blue-black — starch present" },
  "Bread,Iodine":             { c: "#1e40af", d: "Blue-black — starch present" },
  "Egg white,Biuret":         { c: "#7c3aed", d: "Violet — protein present" },
  "Protein (egg albumin),Biuret": { c: "#7c3aed", d: "Violet — protein" },
  "Butter,Grease-spot":       { c: "#fef9c3", d: "Translucent grease spot — fat" },
  "Fat (oil),Grease-spot":    { c: "#fef9c3", d: "Grease spot — fat" },
};
const FoodTestSim = ({ food = "Bread", test = "Iodine" }) => {
  const info = foodMap[`${food},${test}`] || { c: "#94a3b8", d: "No reaction — negative." };
  return <ReactionCardSim title={`${food} + ${test}`} subtitle={info.d} color={info.c} />;
};

/* ---------- ACID / ALKALI ---------- */
const AcidAlkaliSim = ({ solution = "Solution A", test = "Blue litmus" }) => {
  const map = {
    "Solution A,Blue litmus":     { c: "#ef4444", d: "Blue turns RED — acidic" },
    "Solution A,Red litmus":      { c: "#dc2626", d: "No change — acidic" },
    "Solution A,Phenolphthalein": { c: "#f8fafc", d: "Colourless — acidic" },
    "Solution A,Methyl orange":   { c: "#f97316", d: "Red — strongly acidic" },
    "Solution B,Blue litmus":     { c: "#1d4ed8", d: "No change — basic" },
    "Solution B,Red litmus":      { c: "#1e40af", d: "Red turns BLUE — basic" },
    "Solution B,Phenolphthalein": { c: "#ec4899", d: "Pink — basic" },
    "Solution B,Methyl orange":   { c: "#fbbf24", d: "Yellow — basic" },
  };
  const info = map[`${solution},${test}`] || { c: "#94a3b8", d: "" };
  return <ReactionCardSim title={`${solution} · ${test}`} subtitle={info.d} color={info.c} />;
};

/* ---------- VOLTAIC CELL ---------- */
const VoltaicCellSim = ({ cuConc = 1.0, znConc = 1.0 }) => {
  const emf = 1.10 - (0.0591 / 2) * Math.log10(znConc / Math.max(cuConc, 0.001));
  return (
    <Frame>
      <div className="absolute inset-0 flex items-end justify-center gap-8 p-6">
        {/* Zn beaker */}
        <div className="flex flex-col items-center">
          <div className="w-24 h-32 rounded-b-lg border-2 border-slate-500 bg-slate-200/40 flex flex-col-reverse relative overflow-hidden">
            <div className="h-3/4 bg-slate-100/60" />
            <div className="absolute left-1/2 -translate-x-1/2 top-2 w-3 h-24 bg-slate-400" />
          </div>
          <div className="text-xs text-cyan-300 font-mono mt-1">Zn / {znConc}M</div>
        </div>
        {/* Salt bridge */}
        <div className="mb-16">
          <div className="w-24 h-6 bg-amber-300 rounded-t border-2 border-amber-500" />
        </div>
        {/* Cu beaker */}
        <div className="flex flex-col items-center">
          <div className="w-24 h-32 rounded-b-lg border-2 border-slate-500 bg-blue-500/30 flex flex-col-reverse relative overflow-hidden">
            <div className="h-3/4 bg-blue-500/50" />
            <div className="absolute left-1/2 -translate-x-1/2 top-2 w-3 h-24 bg-orange-500" />
          </div>
          <div className="text-xs text-cyan-300 font-mono mt-1">Cu / {cuConc}M</div>
        </div>
      </div>
      {/* Voltmeter */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg bg-slate-800 border-2 border-cyan-400 font-mono text-cyan-300 text-lg">
        {emf.toFixed(3)} V
      </div>
    </Frame>
  );
};

/* ---------- CHROMATOGRAPHY ---------- */
const ChromatoSim = ({ sample = "Green leaf extract", runTime = 25 }) => {
  const t = Math.min(1, runTime / 40);
  const bands = sample.startsWith("Green")
    ? [{ y: 0.95, c: "#f59e0b", name: "Carotene" }, { y: 0.65, c: "#065f46", name: "Chlorophyll a" }, { y: 0.5, c: "#84cc16", name: "Chlorophyll b" }, { y: 0.3, c: "#fde047", name: "Xanthophyll" }]
    : sample === "Autumn leaf extract"
    ? [{ y: 0.9, c: "#fb923c", name: "Carotene" }, { y: 0.55, c: "#eab308", name: "Xanthophyll" }, { y: 0.25, c: "#dc2626", name: "Anthocyanin" }]
    : [{ y: 0.9, c: "#f43f5e", name: "Dye 1" }, { y: 0.5, c: "#3b82f6", name: "Dye 2" }, { y: 0.25, c: "#22c55e", name: "Dye 3" }];
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-6">
        <div className="relative w-24 h-72 bg-slate-100 border-2 border-slate-400 rounded overflow-hidden">
          <div className="absolute bottom-0 inset-x-0 h-4 border-t border-dashed border-slate-500" />
          {bands.map((b, i) => <div key={i} className="absolute left-0 right-0 h-3 rounded" style={{ bottom: `${b.y * t * 100}%`, background: b.c, opacity: t }} />)}
        </div>
        <div className="text-xs font-mono text-cyan-200 space-y-1">
          {bands.map((b, i) => <div key={i} className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded" style={{ background: b.c }} />{b.name} (Rf ≈ {b.y.toFixed(2)})</div>)}
        </div>
      </div>
      <Info items={[`Sample: ${sample}`, `t = ${runTime} min`]} />
    </Frame>
  );
};

/* ---------- REDOX TITRATION ---------- */
const RedoxSim = ({ oxalicConc = 0.1, oxalicVol = 20, kmno4Conc = 0.02 }) => {
  const vNeeded = (5 * oxalicConc * oxalicVol) / (2 * Math.max(kmno4Conc, 0.001));
  const [added, setAdded] = React.useState(0);
  React.useEffect(() => {
    let raf, start = performance.now();
    const step = (now) => {
      const t = (now - start) / 60;
      if (t <= vNeeded) { setAdded(t); raf = requestAnimationFrame(step); }
      else setAdded(vNeeded);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [vNeeded]);
  const near = Math.abs(added - vNeeded) < 0.5;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-end justify-center gap-8 p-6">
        <div className="flex flex-col items-center">
          <div className="w-8 h-40 bg-slate-800/70 border-2 border-slate-500 rounded-b overflow-hidden flex flex-col-reverse">
            <div className="bg-purple-500/80 transition-all" style={{ height: `${100 - (added / vNeeded) * 100}%` }} />
          </div>
          <div className="text-xs font-mono text-purple-300 mt-1">KMnO₄ added: {added.toFixed(1)} mL</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-40 h-40 rounded-b-full border-2 border-slate-500 relative overflow-hidden transition-colors" style={{ background: near ? "linear-gradient(to top, #a855f7, #c084fc, transparent)" : "linear-gradient(to top, rgba(203,213,225,.3), transparent)" }} />
          <div className="text-xs font-mono text-cyan-300 mt-1">Oxalic acid + H₂SO₄</div>
        </div>
      </div>
      <div className="absolute top-4 left-4 right-4 text-center">
        <span className={near ? "text-purple-300 font-bold" : "text-slate-400"} style={{ fontFamily: "monospace" }}>
          {near ? "Endpoint — permanent pale pink!" : "Titrating..."}
        </span>
      </div>
    </Frame>
  );
};

/* ---------- RATE OF REACTION ---------- */
const RateSim = ({ concentration = 0.1, temperature = 25 }) => {
  const time = 60 / (concentration * 10) * Math.exp((25 - temperature) / 30);
  const data = useMemo(() => Array.from({ length: 20 }, (_, i) => ({ c: (0.02 + i*0.01).toFixed(2), rate: (1 / (60 / ((0.02 + i*0.01) * 10) * Math.exp((25 - temperature) / 30))).toFixed(4) })), [temperature]);
  return (
    <Frame>
      <div className="absolute inset-0 p-4 grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="w-40 h-40 rounded-b-full border-2 border-slate-500 bg-yellow-100/40 relative overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-4xl text-yellow-900 font-bold" style={{ opacity: Math.min(1, time / 60) }}>✕</div>
            </div>
          </div>
          <div className="text-xs font-mono text-amber-300">Cross visible for {time.toFixed(1)} s</div>
        </div>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="c" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Line type="monotone" dataKey="rate" stroke="#22d3ee" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Info items={[`[Na₂S₂O₃] = ${concentration} M`, `T = ${temperature} °C`]} />
    </Frame>
  );
};

/* ---------- SOIL STUDY ---------- */
const soilProps = {
  Sandy: { color: "#fbbf24", texture: "Coarse", moisture: 20, humus: 10, whc: 30, ph: 6.0 },
  Clayey: { color: "#a16207", texture: "Fine", moisture: 60, humus: 25, whc: 85, ph: 7.5 },
  Loamy: { color: "#78350f", texture: "Balanced", moisture: 45, humus: 60, whc: 65, ph: 6.8 },
};
const SoilStudySim = ({ soilA = "Sandy", soilB = "Loamy" }) => {
  const A = soilProps[soilA], B = soilProps[soilB];
  return (
    <Frame>
      <div className="absolute inset-0 p-4 grid grid-cols-2 gap-4">
        {[[soilA, A], [soilB, B]].map(([name, p], idx) => (
          <div key={idx} className="flex flex-col items-center gap-2">
            <div className="w-24 h-24 rounded-full shadow-inner" style={{ background: p.color }} />
            <div className="text-cyan-200 font-outfit font-bold">{name}</div>
            <div className="text-[10px] font-mono text-slate-400 space-y-0.5 text-left">
              <div>Texture: {p.texture}</div>
              <div>Moisture: {p.moisture}%</div>
              <div>Humus: {p.humus}%</div>
              <div>WHC: {p.whc}%</div>
              <div>pH: {p.ph}</div>
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
};

/* ---------- ENZYME ---------- */
const EnzymeSim = ({ temperature = 37, pH = 7 }) => {
  const activity = Math.max(0, 100 - Math.abs(temperature - 37) * 3 - Math.abs(pH - 7) * 15);
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div className="relative w-64 h-4 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: `${activity}%` }} />
        </div>
        <div className="text-cyan-200 font-mono">Amylase activity: {activity.toFixed(0)}%</div>
        <div className="text-xs text-slate-400 font-mono">T = {temperature} °C · pH = {pH}</div>
        <div className="mt-2 text-xs text-amber-300 font-mono max-w-md text-center">
          {activity > 80 ? "Optimum: fastest starch → maltose conversion." : activity > 30 ? "Sub-optimal — slower activity." : "Enzyme denatured or inactive."}
        </div>
      </div>
    </Frame>
  );
};

/* ---------- FUNCTIONAL GROUP / BIOMOL / QUALITATIVE — reuse ReactionCardSim ---------- */
const funcGroupMap = {
  "Glycerol (-OH)":        { c: "#dc2626", d: "Ceric ammonium nitrate → red colour confirms -OH" },
  "Formaldehyde (-CHO)":   { c: "#94a3b8", d: "Tollens' → silver mirror on tube walls" },
  "Acetone (C=O)":         { c: "#fbbf24", d: "2,4-DNP → yellow-orange ppt; iodoform → yellow crystals" },
  "Benzoic acid (-COOH)":  { c: "#22c55e", d: "NaHCO₃ → brisk CO₂ effervescence" },
  "Aniline (-NH₂)":        { c: "#f97316", d: "Diazotization + β-naphthol → orange azo dye" },
};
const FuncGroupSim = ({ compound = "Formaldehyde (-CHO)" }) => {
  const info = funcGroupMap[compound] || { c: "#94a3b8", d: "See functional group table." };
  return <ReactionCardSim title={compound} subtitle={info.d} color={info.c} />;
};
const biomolMap = {
  "Glucose,Molisch":       { c: "#7c3aed", d: "Violet ring at junction — carbohydrate" },
  "Glucose,Fehling's":     { c: "#dc2626", d: "Brick red ppt — reducing sugar" },
  "Glucose,Biuret":        { c: "#93c5fd", d: "No colour — not protein" },
  "Powdered milk,Molisch": { c: "#94a3b8", d: "Faint violet — contains carbs (lactose)" },
  "Powdered milk,Biuret":  { c: "#7c3aed", d: "Violet — protein confirmed" },
  "Powdered milk,Fehling's": { c: "#94a3b8", d: "No red ppt — non-reducing" },
  "Powdered milk,Xanthoproteic": { c: "#facc15", d: "Yellow → orange (with NH₃) — aromatic amino acids" },
};
const BiomolSim = ({ sample = "Glucose", test = "Molisch" }) => {
  const info = biomolMap[`${sample},${test}`] || { c: "#94a3b8", d: "" };
  return <ReactionCardSim title={`${sample} + ${test}`} subtitle={info.d} color={info.c} />;
};

/* ---------- MISC placeholders — informational cards ---------- */
const InfoCard = ({ heading, lines }) => (
  <Frame>
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="font-outfit font-black text-2xl text-cyan-200">{heading}</div>
      {lines.map((l, i) => <div key={i} className="text-slate-300 font-mono text-sm max-w-md">{l}</div>)}
    </div>
  </Frame>
);

/* ---------- GALVANOMETER conversion (visual) ---------- */
const GalvSim = ({ mode, Ig = 5, G = 50, target = 2 }) => {
  const Ig_A = Ig / 1000;
  const value = mode === "ammeter" ? (Ig_A * G) / (target - Ig_A) : target / Ig_A - G;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-6">
        <div className="flex flex-col items-center">
          <div className="w-28 h-28 rounded-full bg-slate-800 border-4 border-slate-700 flex items-center justify-center relative">
            <div className="text-cyan-300 font-outfit font-black text-xl">G</div>
            <div className="absolute top-2 right-2 text-[10px] text-slate-400 font-mono">{G}Ω</div>
          </div>
          <div className="text-xs font-mono text-cyan-300 mt-2">Ig = {Ig} mA</div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            {mode === "ammeter" ? "Shunt Rs" : "Series R"}
          </div>
          <div className="px-4 py-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400 text-fuchsia-200 font-mono">
            {value.toFixed(3)} Ω
          </div>
          <div className="text-slate-500 font-mono text-[10px]">
            {mode === "ammeter" ? "parallel across G" : "in series with G"}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-28 h-28 rounded-full bg-amber-500/20 border-4 border-amber-400 flex items-center justify-center">
            <div className="text-amber-200 font-outfit font-black text-xl">{mode === "ammeter" ? "A" : "V"}</div>
          </div>
          <div className="text-xs font-mono text-amber-300 mt-2">Range: {target} {mode === "ammeter" ? "A" : "V"}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- COMPONENT ID (visual multimeter) ---------- */
const componentIcons = {
  "Diode":            { icon: "◀|", desc: "One-way current: forward-bias ~0.7 V", color: "#94a3b8" },
  "LED":              { icon: "◀|✨", desc: "Glows when forward-biased (1.8–3.3 V)", color: "#f472b6" },
  "Transistor (npn)": { icon: "─||─", desc: "3 leads — BE & BC show diode readings", color: "#22d3ee" },
  "Transistor (pnp)": { icon: "─||─", desc: "3 leads — reversed polarity vs npn", color: "#fbbf24" },
  "Resistor":         { icon: "╱╲╱╲", desc: "Same reading both directions (linear)", color: "#84cc16" },
  "Capacitor":        { icon: "─||─", desc: "Reading rises then settles high (charging)", color: "#a78bfa" },
  "IC":               { icon: "▭▭▭", desc: "Multiple pins — identify by part number", color: "#f97316" },
};
const ComponentIdSim = ({ component = "Diode" }) => {
  const info = componentIcons[component] || componentIcons["Diode"];
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div className="w-40 h-40 rounded-2xl border-4 flex items-center justify-center font-outfit font-black text-5xl" style={{ borderColor: info.color, color: info.color, background: `${info.color}22` }}>
          {info.icon}
        </div>
        <div className="text-center">
          <div className="font-outfit font-bold text-cyan-200 text-lg">{component}</div>
          <div className="text-slate-400 font-mono text-xs max-w-sm mt-1">{info.desc}</div>
        </div>
        {/* Simulated multimeter reading */}
        <div className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 font-mono text-emerald-300 text-sm">
          Multimeter → {component === "Diode" ? "0.65 V" : component === "LED" ? "1.9 V" : component === "Resistor" ? "220 Ω" : component === "Capacitor" ? "OL → ↑↑" : component.startsWith("Transistor") ? "BE: 0.7 V · BC: 0.7 V" : "—"}
        </div>
      </div>
    </Frame>
  );
};

/* ---------- METRE BRIDGE COMBO (visual) ---------- */
const MetreComboSim = ({ R1 = 4, R2 = 6, mode = "Series" }) => {
  const R = mode === "Series" ? R1 + R2 : (R1 * R2) / (R1 + R2);
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
        <div className="flex items-center gap-4">
          {mode === "Series" ? (
            <>
              <div className="px-4 py-3 rounded-lg bg-fuchsia-500/20 border-2 border-fuchsia-400 font-mono text-fuchsia-200">R₁ = {R1} Ω</div>
              <div className="w-8 h-0.5 bg-slate-400" />
              <div className="px-4 py-3 rounded-lg bg-cyan-500/20 border-2 border-cyan-400 font-mono text-cyan-200">R₂ = {R2} Ω</div>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-slate-400" />
                <div className="px-4 py-2 rounded-lg bg-fuchsia-500/20 border-2 border-fuchsia-400 font-mono text-fuchsia-200">R₁ = {R1} Ω</div>
                <div className="w-4 h-0.5 bg-slate-400" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-slate-400" />
                <div className="px-4 py-2 rounded-lg bg-cyan-500/20 border-2 border-cyan-400 font-mono text-cyan-200">R₂ = {R2} Ω</div>
                <div className="w-4 h-0.5 bg-slate-400" />
              </div>
            </div>
          )}
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total resistance</div>
          <div className="font-outfit font-black text-3xl text-amber-300">R = {R.toFixed(2)} Ω</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- CONVEX MIRROR (visual) ---------- */
const ConvexMirrorSim = ({ R = 20 }) => (
  <Frame>
    <svg viewBox="0 0 500 300" className="w-full h-full">
      <line x1="20" y1="150" x2="480" y2="150" stroke="#475569" />
      {/* Convex mirror (bulging right) */}
      <path d="M 350 60 Q 320 150 350 240" fill="none" stroke="#22d3ee" strokeWidth="3" />
      <line x1="350" y1="60" x2="360" y2="55" stroke="#22d3ee" />
      <line x1="350" y1="240" x2="360" y2="245" stroke="#22d3ee" />
      {/* Centre C and focus F */}
      <circle cx={350 - R*4} cy="150" r="4" fill="#f472b6" />
      <text x={350 - R*4} y="140" fill="#f472b6" textAnchor="middle" fontSize="12" fontFamily="monospace">C</text>
      <circle cx={350 - R*2} cy="150" r="3" fill="#fde047" />
      <text x={350 - R*2} y="140" fill="#fde047" textAnchor="middle" fontSize="12" fontFamily="monospace">F</text>
      {/* Pin */}
      <line x1={350 - R*4} y1="150" x2={350 - R*4} y2="100" stroke="#fb923c" strokeWidth="3" />
    </svg>
    <Info items={[`R = ${R} cm`, `f = R/2 = ${(R/2).toFixed(1)} cm`]} />
  </Frame>
);

/* ---------- TRAVELLING MICROSCOPE (visual) ---------- */
const TravMicroSim = ({ realDepth = 3, apparentDepth = 2 }) => {
  const n = apparentDepth ? (realDepth / apparentDepth) : 1.5;
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-8">
        <div className="relative">
          {/* Slab */}
          <div className="w-40 border-2 border-cyan-400 bg-cyan-500/15" style={{ height: `${realDepth * 25}px` }}>
            <div className="absolute inset-x-4 top-1 border-t border-dashed border-fuchsia-300" style={{ top: `${apparentDepth * 25}px` }}>
              <div className="text-[10px] font-mono text-fuchsia-300 -mt-3">apparent</div>
            </div>
          </div>
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-mono text-cyan-300">top of glass</div>
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-mono text-amber-300">ink mark (real depth)</div>
        </div>
        <div className="text-cyan-200 font-mono text-sm space-y-1">
          <div>Real depth: {realDepth} cm</div>
          <div>Apparent: {apparentDepth} cm</div>
          <div className="text-amber-300 font-outfit font-bold text-lg pt-2 border-t border-slate-700">n = {n.toFixed(3)}</div>
        </div>
      </div>
    </Frame>
  );
};

/* ---------- DISPLACEMENT LENS (visual reuse of ConvexLens style) ---------- */
const DispLensSim = ({ D = 80, d = 30 }) => {
  const f = (D*D - d*d) / (4*D);
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="30" y1="150" x2="470" y2="150" stroke="#475569" />
        {/* Object */}
        <line x1="30" y1="150" x2="30" y2="110" stroke="#fbbf24" strokeWidth="3" />
        <polygon points="24,115 36,115 30,105" fill="#fbbf24" />
        {/* Screen */}
        <rect x="465" y="80" width="5" height="140" fill="#94a3b8" />
        {/* Lens position 1 */}
        <ellipse cx={30 + (250 - d*2)} cy="150" rx="10" ry="60" fill="rgba(34,211,238,0.2)" stroke="#22d3ee" />
        {/* Lens position 2 */}
        <ellipse cx={30 + (250 + d*2)} cy="150" rx="10" ry="60" fill="rgba(244,114,182,0.2)" stroke="#f472b6" strokeDasharray="4 3" />
        <line x1={30 + (250 - d*2)} y1="220" x2={30 + (250 + d*2)} y2="220" stroke="#fde047" strokeWidth="2" />
        <text x="250" y="240" fill="#fde047" textAnchor="middle" fontSize="12" fontFamily="monospace">d = {d} cm</text>
      </svg>
      <Info items={[`D = ${D} cm`, `d = ${d} cm`, `f = (D²−d²)/4D = ${f.toFixed(2)} cm`]} />
    </Frame>
  );
};

/* ---------- COMBO LENS (visual) ---------- */
const ComboLensSim = ({ fAux = 15, fCombo = 10 }) => {
  const f1 = 1 / (1/fCombo - 1/fAux);
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="30" y1="150" x2="470" y2="150" stroke="#475569" />
        <ellipse cx="220" cy="150" rx="12" ry="70" fill="rgba(34,211,238,0.2)" stroke="#22d3ee" strokeWidth="2" />
        <ellipse cx="260" cy="150" rx="12" ry="70" fill="rgba(244,114,182,0.2)" stroke="#f472b6" strokeWidth="2" />
        <text x="220" y="240" fill="#22d3ee" textAnchor="middle" fontSize="11" fontFamily="monospace">Unknown f₁</text>
        <text x="260" y="255" fill="#f472b6" textAnchor="middle" fontSize="11" fontFamily="monospace">Aux f₂ = {fAux}</text>
      </svg>
      <Info items={[`F = ${fCombo} cm`, `f₂ = ${fAux} cm`, `⇒ f₁ = ${isFinite(f1) ? f1.toFixed(2) : "—"} cm`]} />
    </Frame>
  );
};

/* ---------- CONCAVE LENS (visual) ---------- */
const ConcaveLensSim = ({ u2 = 12, v2 = 25 }) => {
  const f = 1 / (1/v2 - 1/(-u2));
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="30" y1="150" x2="470" y2="150" stroke="#475569" />
        {/* Concave lens */}
        <path d="M 250 80 Q 240 150 250 220 M 250 80 Q 260 150 250 220" fill="none" stroke="#f472b6" strokeWidth="2" />
        <line x1={250 - u2*4} y1="150" x2={250 - u2*4} y2="100" stroke="#fbbf24" strokeWidth="3" />
        <text x={250 - u2*4} y="240" fill="#fbbf24" textAnchor="middle" fontSize="10" fontFamily="monospace">virtual O</text>
        <line x1={250 + v2*4} y1="150" x2={250 + v2*4} y2="120" stroke="#22d3ee" strokeWidth="3" />
        <text x={250 + v2*4} y="240" fill="#22d3ee" textAnchor="middle" fontSize="10" fontFamily="monospace">image</text>
      </svg>
      <Info items={[`u₂ = ${u2} cm`, `v₂ = ${v2} cm`, `f = ${f.toFixed(2)} cm (diverging)`]} />
    </Frame>
  );
};

/* ---------- CONCAVE MIRROR UV (reuse convex-lens style) ---------- */
const ConcaveMirrorUvSim = ({ u = 30, focalLength = 15 }) => {
  const v = 1 / (1/focalLength - 1/u) || 100;
  return (
    <Frame>
      <svg viewBox="0 0 500 300" className="w-full h-full">
        <line x1="20" y1="150" x2="480" y2="150" stroke="#475569" />
        <path d="M 350 60 Q 380 150 350 240" fill="none" stroke="#22d3ee" strokeWidth="3" />
        <circle cx={350 - focalLength * 4} cy="150" r="3" fill="#fde047" />
        <line x1={350 - u*4} y1="150" x2={350 - u*4} y2="110" stroke="#fbbf24" strokeWidth="3" />
        <line x1={350 - v*4} y1="150" x2={350 - v*4} y2="190" stroke="#f472b6" strokeWidth="3" />
      </svg>
      <Info items={[`u = ${u} cm`, `v = ${v.toFixed(1)} cm`, `f = ${focalLength} cm`]} />
    </Frame>
  );
};

/* ---------- DISPATCH ---------- */
export const SimulationCanvas = ({ experiment, params }) => {
  const kind = experiment.sim_kind;
  const P = params;
  switch (kind) {
    case "pendulum": return <PendulumSim {...P} />;
    case "ohm": return <OhmSim {...P} />;
    case "reflection": return <ReflectionSim {...P} />;
    case "density": return <DensitySim {...P} />;
    case "convex-lens": return <ConvexLensSim {...P} />;
    case "prism": return <PrismSim {...P} />;
    case "lever": return <LeverSim {...P} />;
    case "boyle": return <BoyleSim {...P} />;
    case "friction": return <FrictionSim {...P} />;
    case "meterbridge": return <MeterBridgeSim {...P} />;
    case "diode": return <DiodeSim {...P} />;
    case "ph": return <PhSim {...P} />;
    case "titration": return <TitrationSim {...P} />;
    case "sodium-water": return <SodiumSim {...P} />;
    case "electrolysis": return <ElectrolysisSim {...P} />;
    case "microscope": return <MicroscopeSim {...P} />;
    case "photosynthesis": return <PhotoSim {...P} />;
    case "osmosis": return <OsmosisSim {...P} />;
    case "dna-extract": return <DnaSim {...P} />;
    // new kinds
    case "vernier": return <VernierSim {...P} />;
    case "screw-gauge": return <ScrewGaugeSim {...P} />;
    case "heat-action": return <HeatActionSim {...P} />;
    case "flame-test": return <FlameTestSim {...P} />;
    case "double-displacement": return <DoubleDispSim {...P} />;
    case "acid-action": return <AcidActionSim {...P} />;
    case "cation-test": return <CationTestSim {...P} />;
    case "food-test": return <FoodTestSim {...P} />;
    case "acid-alkali": return <AcidAlkaliSim {...P} />;
    case "voltaic-cell": return <VoltaicCellSim {...P} />;
    case "chromatography": return <ChromatoSim {...P} />;
    case "redox-titration": return <RedoxSim {...P} />;
    case "rate-reaction": return <RateSim {...P} />;
    case "soil-study": return <SoilStudySim {...P} />;
    case "enzyme": return <EnzymeSim {...P} />;
    case "functional-group": return <FuncGroupSim {...P} />;
    case "biomol-test": return <BiomolSim {...P} />;
    // NEW physics XII visual renderers
    case "galv-ammeter": return <GalvSim mode="ammeter" Ig={P.Ig} G={P.G} target={P.I} />;
    case "galv-voltmeter": return <GalvSim mode="voltmeter" Ig={P.Ig} G={P.G} target={P.V} />;
    case "component-id": return <ComponentIdSim {...P} />;
    case "metre-combo": return <MetreComboSim {...P} />;
    case "convex-mirror": return <ConvexMirrorSim {...P} />;
    case "travelling-microscope": return <TravMicroSim {...P} />;
    case "displacement-lens": return <DispLensSim {...P} />;
    case "combo-lens": return <ComboLensSim {...P} />;
    case "concave-lens": return <ConcaveLensSim {...P} />;
    case "concave-mirror-uv": return <ConcaveMirrorUvSim {...P} />;
    // Info-card fallbacks for observational/qualitative experiments
    case "hard-water": return <InfoCard heading={`${P.sample || 'Water sample'}`} lines={[`Treatment: ${P.treatment || 'None'}`, 'See conclusion for expected observation.']} />;
    case "displacement": return <InfoCard heading="Volume by Displacement" lines={[`Radius r = ${P.radius} cm → V = (4/3)πr³`, `Initial water = ${P.initV} mL`, 'Immerse the bob → note new level.']} />;
    case "thermograph": return <InfoCard heading="TC vs TF" lines={[`TC = ${P.tempC} °C`, `TF = ${(1.8 * P.tempC + 32).toFixed(1)} °F`, 'Slope = 1.8, intercept = 32.']} />;
    case "mirror-distance": return <InfoCard heading="Plane Mirror" lines={[`Object distance = ${P.objectDist} cm`, `Image distance behind mirror = ${P.objectDist} cm (equal)`]} />;
    case "concave-mirror": return <InfoCard heading="Concave Mirror" lines={[`Focal length f = ${P.focalLength} cm`, 'Image of distant object → f from mirror']} />;
    case "series-parallel": return <InfoCard heading={P.mode || 'Circuit mode'} lines={[`V = ${P.voltage} V`, P.mode === 'Series' ? 'Both bulbs dimmer (total R doubles)' : P.mode === 'Parallel' ? 'Each bulb has full V — normal brightness' : 'Single bulb glows normally']} />;
    case "magnetic-field": return <InfoCard heading={`Magnet: ${P.orientation}`} lines={['Neutral points on equatorial line', 'Field lines curve N → S outside magnet']} />;
    case "archimedes": return <InfoCard heading="Archimedes' Principle" lines={[`Ball V = ${P.volume} cm³, ρ = ${P.density} g/cm³`, `Upthrust = ρ_water · V · g`, 'Verified: W_air − W_water = weight of displaced water']} />;
    case "pulley": return <InfoCard heading="Pulley System" lines={[`${P.pulleys} pulleys · VR = ${P.pulleys}`, `Load = ${P.load} N, η = ${P.efficiency}%`, `Effort = Load / (VR·η) = ${(P.load / (P.pulleys * P.efficiency / 100)).toFixed(1)} N`]} />;
    case "glass-slab": return <InfoCard heading="Rectangular Glass Slab" lines={[`i = ${P.incidence}°, n = ${P.refractiveIndex}, t = ${P.thickness} cm`, 'Emergent ray parallel to incident ray', 'Lateral shift = t·sin(i−r)/cos(r)']} />;
    case "calorimeter": return <InfoCard heading="Method of Mixtures" lines={[`Metal ${P.massMetal} g at ${P.tempHot} °C`, `Water ${P.massWater} g at ${P.tempCold} °C`, 'Heat lost = heat gained']} />;
    case "latent-heat": return <InfoCard heading="Latent Heat of Ice" lines={[`Ice ${P.massIce} g, water ${P.massWater} g at ${P.tempInit} °C`, 'L ≈ 334 J/g (theoretical)']} />;
    case "household-wiring": return <InfoCard heading="Ring Main Circuit" lines={[`Load = ${P.load} A · Fuse = ${P.fuseRating} A`, P.load > P.fuseRating ? '⚠ FUSE BLOWS — circuit protected' : '✓ Circuit intact']} />;
    case "spherometer": return <InfoCard heading="Radius of Curvature" lines={[`l = ${P.distL} cm, h = ${P.sagitta} mm`, `R = l²/(6h) + h/2 = ${((P.distL**2)/(6*P.sagitta/10) + (P.sagitta/10)/2).toFixed(2)} cm`]} />;
    case "parallelogram": return <InfoCard heading="Parallelogram Law" lines={[`P = ${P.P} N, Q = ${P.Q} N, θ = ${P.angle}°`, `R = √(P² + Q² + 2PQ·cosθ) = ${Math.sqrt(P.P**2 + P.Q**2 + 2*P.P*P.Q*Math.cos(P.angle*PI/180)).toFixed(2)} N`]} />;
    case "spring-oscillator": return <InfoCard heading="Spring Oscillator" lines={[`m = ${P.mass} g, k = ${P.springK} N/m`, `T = 2π√(m/k) = ${(2*Math.PI*Math.sqrt(P.mass/1000/P.springK)).toFixed(3)} s`]} />;
    case "cooling-curve": return <InfoCard heading="Newton's Cooling" lines={[`T₀ = ${P.tempInit} °C → T∞ = ${P.tempAmb} °C`, `k = ${P.coolingRate} min⁻¹`, 'T(t) approaches ambient exponentially']} />;
    case "resonance-column": return <InfoCard heading="Resonance Column" lines={[`f = ${P.frequency} Hz, d = ${P.diameter} cm`, 'v_sound ≈ 343 m/s (room T)']} />;
    case "viscosity": return <InfoCard heading="Terminal Velocity" lines={[`r = ${P.radius} mm, ρ = ${P.densityBall}, σ = ${P.densityLiquid}`, `η = ${P.eta} Pa·s`]} />;
    case "liquid-n": return <InfoCard heading="Refractive Index of Liquid" lines={[`f_convex = ${P.focalConvex} cm`, `f_combo = ${P.focalComb} cm`]} />;
    case "potentiometer": return <InfoCard heading="Potentiometer" lines={[`l₁ = ${P.l1} cm, l₂ = ${P.l2} cm, R = ${P.R} Ω`, `r = R·(l₁−l₂)/l₂ = ${(P.R*(P.l1-P.l2)/P.l2).toFixed(3)} Ω`]} />;
    case "capacitor": return <InfoCard heading="RC Charging" lines={[`R = ${P.R} kΩ, C = ${P.C} μF`, `τ = RC = ${(P.R*P.C/1000).toFixed(1)} ms`, `V₀ = ${P.V0} V`]} />;
    case "glass-working": return <InfoCard heading={P.task || 'Glass working'} lines={['Rotate glass in soft flame', 'Let cool naturally to avoid cracks']} />;
    case "qualitative": return <InfoCard heading={`${P.cation} + ${P.anion}`} lines={['Group precipitation of cations', 'Confirmatory tests for anion']} />;
    case "prep-crystal": return <InfoCard heading={`Prepare ${P.salt}`} lines={['Dissolve in warm water', 'Cool slowly for large crystals']} />;
    case "flower-dissection": return <InfoCard heading="Hibiscus Dissection" lines={[`Highlight: ${P.part}`, 'K5 C5 A(∞) G(5)', 'Complete, hermaphrodite flower']} />;
    case "germination": return <InfoCard heading={`${P.seed} — day ${P.day}`} lines={[P.day >= 2 ? 'Radicle emerges first, then plumule' : 'Seed absorbs water and swells']} />;
    case "classification": return <InfoCard heading={P.group || 'Animal group'} lines={['See conclusion for defining characters and example']} />;
    case "belljar": return <InfoCard heading={`Diaphragm: ${P.diaphragm}`} lines={[P.diaphragm === 'Contracted (inhale)' ? 'Balloons inflate — inhalation' : 'Balloons deflate — exhalation']} />;
    case "mitosis": return <InfoCard heading={`Phase: ${P.phase}`} lines={['See conclusion for phase description']} />;
    case "diffusion": return <InfoCard heading="KMnO₄ Diffusion" lines={[`t = ${P.time} min, T = ${P.temperature} °C`, 'Purple colour spreads through water']} />;
    case "transpiration": return <InfoCard heading="Transpiration" lines={[`Humidity ${P.humidity}%, T = ${P.temperature} °C`, 'Water vapour condenses on bell-jar walls']} />;
    case "blood-cells": return <InfoCard heading={`Highlight: ${P.cell}`} lines={['See conclusion for function']} />;
    case "beetroot": return <InfoCard heading="Beetroot Membrane" lines={[`T = ${P.temperature} °C, alcohol = ${P.alcohol}%`, 'Betalain leaks as membrane disrupts']} />;
    case "plasmolysis": return <InfoCard heading="Plasmolysis" lines={[`NaCl = ${P.salt}%`, P.salt < 2 ? 'Turgid cells' : P.salt < 6 ? 'Incipient plasmolysis' : 'Full plasmolysis']} />;
    case "floral-family": return <InfoCard heading={P.family} lines={['See conclusion for floral formula']} />;
    case "stomata": return <InfoCard heading={P.leafType || 'Stomata'} lines={[P.leafType && P.leafType.includes('Dicot') ? 'Bean-shaped guard cells' : 'Dumbbell-shaped guard cells']} />;
    default: return <PlaceholderSim />;
  }
};

export default SimulationCanvas;
